import type { ReactNode } from 'react'
import { Icon } from '@/components/icons/icon'
import { cn } from '@/lib/utils'
import { ONBOARDING_STEPS } from './steps'

interface StepRailProps {
  /** 0 is the welcome screen (nothing current), 1–9 a step, past the last the done screen (all done). */
  current: number
  /** Steps finished, shown done and clickable. Defaults to every step before `current`. */
  done?: readonly number[]
  /** Done steps still to finish, marked "To do" (verification left for later). */
  todo?: readonly number[]
  /** Opens a done step. Without it no step is clickable. */
  onJump?: (step: number) => void
  steps?: readonly string[]
  /** The box under the steps (`.ob-aside`). */
  help?: ReactNode
  className?: string
}

/**
 * The left-hand step list (`.ob-rail`): done steps carry a tick and open on click,
 * the current one is raised and `aria-current="step"`, later ones wait. Hidden at
 * ≤960 px, where `MobileProgress` takes its place.
 */
export function StepRail({ current, done, todo = [], onJump, steps = ONBOARDING_STEPS, help, className }: StepRailProps) {
  const finished = current > steps.length
  return (
    <nav className={cn('ob-rail', className)} aria-label="Setup steps">
      <ol>
        {steps.map((label, index) => {
          const step = index + 1
          const isCurrent = step === current
          const isDone = !isCurrent && (finished || (done ? done.includes(step) : step < current))
          const warn = isDone && todo.includes(step)
          const jump = isDone && onJump ? () => onJump(step) : undefined
          return (
            <li key={label} className={cn(isDone && 'ob-done', isCurrent && 'ob-cur', warn && 'ob-warn')}>
              <button
                type="button"
                aria-current={isCurrent ? 'step' : undefined}
                aria-disabled={jump ? undefined : true}
                tabIndex={jump ? undefined : -1}
                onClick={jump}
              >
                <span className="ob-dot" aria-hidden="true">
                  {isDone ? warn ? '!' : <Icon name="check" /> : step}
                </span>
                {label}
                {warn ? <em>To do</em> : isDone ? <span className="sr-only">, done</span> : null}
              </button>
            </li>
          )
        })}
      </ol>
      {help ? <div className="ob-aside">{help}</div> : null}
    </nav>
  )
}
